import {
  Box,
  CircularProgress,
  Grid2 as Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isValid } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { API_TO_MOCK_CAT, ConditionSubfactor } from './ConditionScoreView._mock';

import HealthRings, { HealthRingsDataPoint } from '@/components/common/HealthRings';
import RadarChart, { RadarChartDataSeries } from '@/components/common/RadarChart';
import {
  COLORS,
  COND_RADAR_TOOLTIPS,
  GradeTag,
  HOAChecklistCard,
  IssueRow,
  isHiddenSubscore,
  PermitHistoryCard,
  RepairHorizonCompact,
  ScoreProcessingBanner,
  SCORE_TOOLTIPS,
  ScrollHintList,
  SubscoreDetailCard,
  Tip,
} from '@/components/dashboard/scoring';
import HomeScoreCard, {
  CONDITION_CATEGORIES,
  CONDITION_LABEL_ALIASES,
  CONDITION_RADAR_ORDER,
} from '@/components/home/cards/HomeScoreCard';
import { ManualPricePromptInline } from '@/components/property/ManualPricePromptDialog';
import { UploadReportInline } from '@/components/property/UploadReportModal';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useGetIssuesForProperty } from '@/hooks/issues/useGetIssuesForProperty';
import { mapIssuesToItems } from '@/hooks/issues/useNegotiationLeverage';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useHOAChecklist } from '@/hooks/useHOAChecklist';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import { useUserRole } from '@/hooks/useUserRole';
import {
  DocumentClassificationType,
  DOCUMENT_TYPE_CONFIG,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_SORT_ORDER,
} from '@/models/DocumentRecord';
import { Issue, urgencyOrder } from '@/models/Issue';
import { createDocuments } from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files/api';
import { VALUE_FONT, VALUE_FONT_SMALL, VALUE_TRACKING, VALUE_WEIGHT } from '@/theme/consts';
import { useTranslationPrefix } from '@/translations/i18n';
import { parseDate } from '@/utils/formatDate';

const URGENCY_TIPS: Record<string, string> = {
  Critical:
    'Significant issue that risks further damage if delayed. Worth addressing within the first 30 days.',
  Urgent:
    'Important repair that should be scheduled soon. Delaying beyond 90 days may increase cost or scope.',
  Moderate:
    'Should be addressed within the first year. Not urgent but contributes to overall condition score.',
  Low: 'Minor item with minimal impact. Address when convenient or bundle with other work.',
  Monitor: 'No action required now. Re-evaluate at next inspection cycle or if conditions change.',
};

export const ConditionScoreView = ({
  scorecardOnly = false,
  layout,
}: {
  scorecardOnly?: boolean;
  /** 'horizontal' renders a standalone chart-left/category-rows-right panel
   *  (the expanded EQ condition-details panel) instead of the default
   *  vertical scorecard. Self-contained — doesn't touch HomeScoreCard. */
  layout?: 'horizontal';
}) => {
  const [expandedCondCat, setExpandedCondCat] = useState<string | null>(null);
  const [hoveredCondCat, setHoveredCondCat] = useState<string | null>(null);
  const [vizMode, setVizMode] = useState<'spider' | 'rings'>('spider');
  const [repairPreviewActive, setRepairPreviewActive] = useState(false);
  // Track which subfactor (by issueId) has its description expanded inline.
  // Click toggles — clicking the same item again collapses it.
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const { selectedProperty } = usePropertiesContext();
  const { cardMode: _cardMode } = useDashboardLayout();
  const propertyId = selectedProperty?.property.id;
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { scoreToGrade } = useScoringConfig();

  const {
    isLoading,
    isConditionProcessing,
    isNoListPrice,
    error,
    data,
    conditionSubscoreExplanations,
    conditionCategoryExplanations: _conditionCategoryExplanations,
    conditionTabs,
    conditionScore,
    conditionScoreDrivers,
    conditionRadarData,
    conditionScoredAt,
    propertyDetails,
    permits,
  } = useHauserEQResults(propertyId);
  const { data: hoaData } = useHOAChecklist(propertyId);
  const { stepProcessing, beginProcessingIndicator, resetProcessing } = useUploaderContext();

  const { issues } = useGetIssuesForProperty(propertyId ?? '');
  // Only the horizontal (self-serve) layout needs document records for its
  // "General"/"Pest" badges — passing undefined elsewhere keeps this query
  // disabled (useDocumentRecords hardcodes enabled: Boolean(propertyId)) so
  // the scorecardOnly/full layouts other user types hit don't pay for a
  // fetch they never use.
  const { documentRecords } = useDocumentRecords(layout === 'horizontal' ? propertyId : undefined);

  useEffect(() => {
    setRepairPreviewActive(false);
  }, [propertyId]);

  const apiData = data as Record<string, unknown> | undefined;

  // ─── Horizontal-layout data prep — ported from HomeScoreCard, minus the
  // repair-preview simulation (not applicable to this standalone panel). ───
  const processedDocs = (documentRecords ?? [])
    .filter((r) => r.documentType && DOCUMENT_TYPE_CONFIG[r.documentType]?.group === 'inspection')
    .sort((a, b) => {
      const ai = DOCUMENT_TYPE_SORT_ORDER.indexOf(a.documentType as DocumentClassificationType);
      const bi = DOCUMENT_TYPE_SORT_ORDER.indexOf(b.documentType as DocumentClassificationType);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const grade = conditionScore != null ? scoreToGrade(conditionScore) : null;

  const scoreToColor = (s: number): string =>
    s >= 86
      ? '#f3f3f3'
      : s >= 77
      ? '#5AE9FF'
      : s >= 68
      ? COLORS.moderate
      : s >= 59
      ? COLORS.urgent
      : COLORS.critical;

  // Radar keys with no inspection findings — rendered as N/A axes
  const naRadarKeys = useMemo(() => {
    if (!issues?.length) return new Set<string>();
    const RADAR_KEY_TO_CAT: Record<string, string> = {
      structural: 'STRUCTURAL',
      exterior: 'EXTERIOR',
      interior: 'INTERIOR',
      hvac: 'HVAC_SYSTEMS',
      plumbing: 'PLUMBING',
      electrical: 'ELECTRICAL',
    };
    const withIssues = new Set(
      issues.map((issue) => {
        const upper = issue.category.toUpperCase().replace(/\s+/g, '_');
        return upper === 'HVAC' ? 'HVAC_SYSTEMS' : upper;
      }),
    );
    return new Set(
      CONDITION_RADAR_ORDER.filter((key) => !withIssues.has(RADAR_KEY_TO_CAT[key] ?? '')),
    );
  }, [issues]);

  // Reorder radar data to match the spider graph's fixed layout (clockwise
  // from top: Structural, Exterior, Interior, HVAC, Plumbing, Electrical)
  const radarData = useMemo(() => {
    if (!conditionRadarData || conditionRadarData.length !== 6) return conditionRadarData;
    const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '');
    const byKey = Object.fromEntries(
      conditionRadarData.map((d) => {
        const k = norm(d.label);
        return [CONDITION_LABEL_ALIASES[k] ?? k, d];
      }),
    );
    return CONDITION_RADAR_ORDER.map((key) => {
      const point = byKey[key] ?? conditionRadarData[0];
      return { ...point, isNa: naRadarKeys.has(key) };
    });
  }, [conditionRadarData, naRadarKeys]);

  const series: RadarChartDataSeries[] = useMemo(() => {
    if (!radarData) return [];
    return [
      {
        data: radarData,
        fillColor: `${COLORS.urgent}25`,
        strokeWidth: 3,
        segmentColors: radarData.map((point) => scoreToColor(point.value)),
      },
    ];
  }, [radarData]);

  const healthRingsData: HealthRingsDataPoint[] = useMemo(() => {
    if (!radarData) return [];
    return radarData.map((point, i) => ({
      label: CONDITION_CATEGORIES[i] ?? point.label,
      value: point.value,
      color: scoreToColor(point.value),
      isNa: point.isNa,
    }));
  }, [radarData]);

  // Group real issues by category
  const issuesByCategory = useMemo(() => {
    if (!issues?.length) return null;
    const grouped: Record<string, Issue[]> = {};
    for (const issue of issues) {
      const upper = issue.category.toUpperCase().replace(/\s+/g, '_');
      const catKey = upper === 'HVAC' ? 'HVAC_SYSTEMS' : upper;
      if (!grouped[catKey]) grouped[catKey] = [];
      grouped[catKey].push(issue);
    }
    // Sort issues within each category by urgency
    for (const catKey of Object.keys(grouped)) {
      grouped[catKey].sort(
        (a, b) => (urgencyOrder[a.urgency] ?? 99) - (urgencyOrder[b.urgency] ?? 99),
      );
    }
    return grouped;
  }, [issues]);

  const formatCostRange = (issue: Issue): string => {
    if (issue.costEstimateMin == null && issue.costEstimateMax == null) return 'N/A';
    const sym = issue.currencySymbol ?? '$';
    if (issue.costEstimateMin != null && issue.costEstimateMax != null) {
      return `${sym}${issue.costEstimateMin.toLocaleString()} – ${sym}${issue.costEstimateMax.toLocaleString()}`;
    }
    const val = issue.costEstimateMin ?? issue.costEstimateMax ?? 0;
    return `${sym}${val.toLocaleString()}`;
  };

  const CATEGORY_KEYS = [
    'STRUCTURAL',
    'EXTERIOR',
    'INTERIOR',
    'HVAC_SYSTEMS',
    'PLUMBING',
    'ELECTRICAL',
  ];

  const categoryData = useMemo(() => {
    const rawScores =
      (apiData?.condition_category_scores as Record<string, number> | undefined) ?? {};
    const apiScores = Object.fromEntries(
      Object.entries(rawScores).map(([k, v]) => [k.toLowerCase(), v]),
    );
    return Object.fromEntries(
      CATEGORY_KEYS.map((key) => {
        const mappedKey = Object.entries(API_TO_MOCK_CAT).find(([, v]) => v === key)?.[0];
        const apiScore = apiScores[mappedKey ?? ''] ?? apiScores[key.toLowerCase()];
        // apiScore can be missing/null even when real issues exist for this category —
        // e.g. a category-key mapping miss on the scoring side. Preserve that as null
        // rather than coercing to 0, so it renders as "–" instead of a false failing grade.
        const score = typeof apiScore === 'number' ? Math.round(apiScore) : null;
        const grade = score !== null ? scoreToGrade(score) : null;

        const realIssues = issuesByCategory?.[key];
        if (realIssues) {
          const subfactors: Record<string, ConditionSubfactor> = {};
          for (const issue of realIssues) {
            subfactors[issue.title] = {
              urgency: issue.urgency,
              bt: (issue.costEstimateMax ?? 0) > 3000,
              cost: formatCostRange(issue),
              issueId: issue.id,
              description: issue.description,
            };
          }
          return [key, { score, grade, issues: realIssues.length, subfactors }];
        }

        return [key, { score, grade, issues: 0, subfactors: {} }];
      }),
    );
  }, [apiData, issuesByCategory]);

  // Map PascalCase subscore keys to snake_case API driver keys
  const SUBSCORE_TO_DRIVER_KEY: Record<string, string> = {
    HomeHealth: 'home_health',
    RepairBurden: 'repair_burden',
  };

  const COND_SUBSCORE_QUESTIONS: Record<string, string> = {
    HomeHealth: 'What kind of shape is this house actually in?',
    RepairBurden: 'How much am I going to have to spend fixing this place?',
  };

  // Build subscore stories from API data only
  const CATEGORY_DISPLAY: Record<string, string> = {
    STRUCTURAL: 'Structural',
    EXTERIOR: 'Exterior',
    INTERIOR: 'Interior',
    HVAC_SYSTEMS: 'HVAC',
    PLUMBING: 'Plumbing',
    ELECTRICAL: 'Electrical',
  };

  const worstPillar = useMemo(() => {
    // Only consider categories with a real, valid score — a category the scoring
    // engine couldn't score (null) can't meaningfully be "the worst."
    const entries = Object.entries(categoryData).filter(
      (entry): entry is [string, (typeof entry)[1] & { score: number; grade: string }] =>
        entry[1].score !== null && entry[1].score > 0,
    );
    if (!entries.length) return null;
    const [key, cat] = entries.reduce((a, b) => (a[1].score <= b[1].score ? a : b));
    const topIssues = Object.entries(cat.subfactors ?? {})
      .sort(([, a], [, b]) => (urgencyOrder[a.urgency] ?? 99) - (urgencyOrder[b.urgency] ?? 99))
      .map(([title, sf]) => ({ title, urgency: sf.urgency, cost: sf.cost }));
    return { label: CATEGORY_DISPLAY[key] ?? key, score: cat.score, grade: cat.grade, topIssues };
  }, [categoryData]);

  // Mapping + specialist-report dedup shared with the negotiation-leverage
  // hook (see useNegotiationLeverage.ts) so this view's Top Concerns list and
  // the Market Insights negotiation callout can't silently disagree.
  const issueList = useMemo(() => mapIssuesToItems(issues), [issues]);

  const repairPreview = useMemo(() => {
    if (conditionScore == null || !issueList.length) {
      return {
        score: null as number | null,
        categoryScores: {} as Partial<Record<string, number>>,
      };
    }

    type TradeGroup = {
      issues: typeof issueList;
      critCount: number;
      urgCount: number;
      modCount: number;
      totalCostMid: number;
    };
    const tradeMap = new Map<string, TradeGroup>();
    for (const issue of issueList) {
      const key = issue.trade ?? issue.category;
      if (!tradeMap.has(key)) {
        tradeMap.set(key, {
          issues: [],
          critCount: 0,
          urgCount: 0,
          modCount: 0,
          totalCostMid: 0,
        });
      }
      const group = tradeMap.get(key);
      if (!group) continue;
      group.issues.push(issue);
      if (issue.urgency === 'Critical') group.critCount++;
      else if (issue.urgency === 'Urgent') group.urgCount++;
      else if (issue.urgency === 'Moderate') group.modCount++;
      group.totalCostMid += issue.costMid;
    }

    const topGroups = [...tradeMap.values()]
      .map((group) => ({
        ...group,
        score:
          group.critCount * 4 + group.urgCount * 3 + group.modCount * 2 + group.totalCostMid / 1000,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

    const normalizeCategory = (category: string) => {
      const key = category.toUpperCase().replace(/\s+/g, '_');
      return key === 'HVAC' ? 'HVAC_SYSTEMS' : key;
    };
    const categoryToRadarKey: Record<string, string> = {
      STRUCTURAL: 'structural',
      EXTERIOR: 'exterior',
      INTERIOR: 'interior',
      HVAC_SYSTEMS: 'hvac',
      HVAC: 'hvac',
      PLUMBING: 'plumbing',
      ELECTRICAL: 'electrical',
    };
    const categoryWeights: Record<string, number> = {
      structural: 1.35,
      exterior: 1.25,
      hvac: 1,
      plumbing: 1,
      electrical: 1,
      interior: 0.8,
    };
    const severityPenalty: Record<string, number> = {
      Critical: 12,
      Urgent: 8,
      Moderate: 4.5,
      Low: 1.25,
      Monitor: 0.35,
    };
    const issuePenalty = (issue: (typeof issueList)[number]) =>
      (severityPenalty[issue.urgency] ?? 1) + Math.min(issue.costMid / 2500, 8);

    const fixedIssues = new Set<(typeof issueList)[number]>();
    for (const group of topGroups) {
      for (const issue of group.issues) {
        fixedIssues.add(issue);
      }
    }

    const categoryScores: Partial<Record<string, number>> = {};
    let weightedLift = 0;
    let appliedWeight = 0;
    for (const [categoryKey, radarKey] of Object.entries(categoryToRadarKey)) {
      if (categoryKey === 'HVAC') continue;
      const raw = categoryData[categoryKey as keyof typeof categoryData];
      const current = raw?.score;
      if (typeof current !== 'number') continue;

      const categoryIssues = issueList.filter(
        (issue) => normalizeCategory(issue.category) === categoryKey,
      );
      const totalPenalty = categoryIssues.reduce((sum, issue) => sum + issuePenalty(issue), 0);
      const fixedPenalty = categoryIssues.reduce(
        (sum, issue) => sum + (fixedIssues.has(issue) ? issuePenalty(issue) : 0),
        0,
      );
      if (totalPenalty <= 0 || fixedPenalty <= 0) continue;

      const repairedShare = Math.min(1, fixedPenalty / totalPenalty);
      const next = Math.min(95, Math.round(current + (95 - current) * repairedShare));
      categoryScores[radarKey] = next;
      const weight = categoryWeights[radarKey] ?? 1;
      weightedLift += (next - current) * weight;
      appliedWeight += weight;
    }

    const avgLift = appliedWeight > 0 ? weightedLift / appliedWeight : 0;
    const projectedScore = Math.min(95, Math.round(conditionScore + avgLift * 0.75));

    return { score: projectedScore, categoryScores };
  }, [categoryData, conditionScore, issueList]);

  const rawUrgencyCounts = useMemo(() => {
    if (!issues?.length) return undefined;
    const counts: Record<string, number> = {};
    for (const issue of issues) {
      if (!issue.resolved) counts[issue.urgency] = (counts[issue.urgency] ?? 0) + 1;
    }
    return counts;
  }, [issues]);

  const condSubscoreStories = useMemo(() => {
    const tabScores = Object.fromEntries((conditionTabs ?? []).map((t) => [t.id, t.score]));
    const explanationsByPascal = Object.fromEntries(
      Object.entries(conditionSubscoreExplanations ?? {}).map(([k, v]) => [
        k
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(''),
        v,
      ]),
    );

    const stories: Record<
      string,
      { question: string; score: number; narrative: string; strengths: string[]; gaps: string[] }
    > = {};
    for (const key of Object.keys(COND_SUBSCORE_QUESTIONS).filter((k) => !isHiddenSubscore(k))) {
      const score = tabScores[key];
      const narrative = explanationsByPascal[key];
      const driverKey = SUBSCORE_TO_DRIVER_KEY[key];
      const drivers = driverKey
        ? conditionScoreDrivers?.[driverKey as keyof typeof conditionScoreDrivers]
        : null;

      if (score != null || narrative || drivers) {
        stories[key] = {
          question: COND_SUBSCORE_QUESTIONS[key],
          score: score ?? 0,
          narrative: narrative ?? '',
          strengths: drivers?.driving_up ?? [],
          gaps: drivers?.driving_down ?? [],
        };
      }
    }
    return stories;
  }, [conditionSubscoreExplanations, conditionTabs, conditionScoreDrivers]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canAccessAdmin: _canAccessAdmin } = useUserRole();

  const hasData = !isLoading;
  const isReportProcessing = isConditionProcessing || (stepProcessing >= 1 && stepProcessing < 5);

  const hasNoReport =
    !isLoading &&
    !isReportProcessing &&
    (!issues || issues.length === 0) &&
    !apiData?.condition_score &&
    !apiData?.condition_category_scores;

  const URGENCY_LEVELS = ['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const;
  const URGENCY_COLORS: Record<string, string> = {
    Critical: COLORS.critical,
    Urgent: COLORS.urgent,
    Moderate: COLORS.moderate,
    Low: COLORS.low,
    Monitor: COLORS.monitor,
  };

  // Inline rows rendered inside the HomeScoreCard — no individual card borders, dividers only
  const renderCategoryRows = () => {
    const entries = Object.entries(categoryData);

    return (
      <div style={{ paddingBottom: 12 }}>
        {entries.map(([key, cat], idx) => {
          const isExpanded = expandedCondCat === key;
          const tooltip = COND_RADAR_TOOLTIPS[key] ?? COND_RADAR_TOOLTIPS[key.replace('_', ' ')];
          const displayKey = key
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .replace(/\bHvac\b/, 'HVAC');
          const hasNoIssues = issuesByCategory !== null && cat.issues === 0;
          // Real issues exist but the scoring engine returned no score for this category
          // (e.g. a category-key mapping miss) — show "–" like hasNoIssues, but keep the
          // issue list itself visible since we do have real issues to show.
          const scoreMissing = cat.score === null;

          // Count issues per urgency level for the mini bar chart
          const urgencyCounts = URGENCY_LEVELS.map(
            (u) => Object.values(cat.subfactors).filter((sf) => sf.urgency === u).length,
          );
          const maxUrgencyCount = Math.max(...urgencyCounts, 1);

          return (
            <div key={key}>
              {/* Inset divider — respects 20px card padding */}
              {idx > 0 && (
                <div
                  style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 20px' }}
                />
              )}

              {/* Clickable header row */}
              <div
                data-testid={`condition-category-${key.toLowerCase().replace(/_/g, '-')}`}
                onClick={() => setExpandedCondCat(isExpanded ? null : key)}
                onMouseEnter={() => setHoveredCondCat(key)}
                onMouseLeave={() => setHoveredCondCat(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 20px',
                  height: 56,
                  cursor: 'pointer',
                  gap: 14,
                }}>
                {/* Category name + issue count */}
                <Tip
                  text={tooltip}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: COLORS.text,
                      lineHeight: 1,
                    }}>
                    {key === 'HVAC_SYSTEMS'
                      ? isMobile
                        ? 'Systems'
                        : 'HVAC / Systems'
                      : displayKey}
                  </span>
                  {cat.issues > 0 && (
                    <span style={{ fontSize: 11, color: COLORS.textDim, marginTop: 3 }}>
                      {cat.issues} {cat.issues === 1 ? 'remark' : 'remarks'}
                    </span>
                  )}
                </Tip>

                <span style={{ flex: 1 }} />

                {/* Mini urgency bar chart */}
                {!hasNoIssues && (
                  // narrow vertical pills on one baseline (the Pipeline card's bars): an empty urgency keeps a short
                  // neutral stub so every row reads as the same five slots
                  <svg
                    width={34}
                    height={22}
                    style={{ flexShrink: 0, alignSelf: 'center', display: 'block' }}>
                    {URGENCY_LEVELS.map((urgency, i) => {
                      const count = urgencyCounts[i];
                      const barH = count === 0 ? 3 : Math.max(5, (count / maxUrgencyCount) * 22);
                      return (
                        <rect
                          key={urgency}
                          x={i * 7.5}
                          y={22 - barH}
                          width={3}
                          height={barH}
                          rx={1.5}
                          fill={count === 0 ? 'var(--tx-fill-3)' : URGENCY_COLORS[urgency]}
                          opacity={count === 0 ? 1 : hoveredCondCat === key ? 1 : 0.5}
                          style={{ transition: 'opacity 0.2s' }}
                        />
                      );
                    })}
                  </svg>
                )}

                {/* Score */}
                <span
                  style={{
                    fontFamily: VALUE_FONT_SMALL,
                    fontSize: 22,
                    fontWeight: 300,
                    color: hasNoIssues || scoreMissing ? 'var(--tx-faint)' : COLORS.text,
                    letterSpacing: '-0.04em',
                    lineHeight: 1,
                    flexShrink: 0,
                    minWidth: 44,
                    textAlign: 'right',
                  }}>
                  {hasNoIssues || scoreMissing ? '–' : cat.score}
                </span>

                {/* Chevron */}
                <span
                  data-testid={`condition-chevron-${key.toLowerCase().replace(/_/g, '-')}`}
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: COLORS.textDim,
                  }}>
                  <ChevronDown size={16} />
                </span>
              </div>

              {/* Expanded: no issues */}
              {isExpanded && hasNoIssues && (
                <div
                  style={{
                    padding: '12px 20px 20px',
                    fontSize: 13,
                    color: COLORS.textDim,
                    fontStyle: 'italic',
                  }}>
                  No findings reported for this category.
                </div>
              )}

              {/* Expanded: issue list */}
              {isExpanded && !hasNoIssues && (
                <ScrollHintList
                  style={{
                    padding: isMobile ? '12px 20px 22px' : '10px 20px 20px',
                    maxHeight: 252,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.12) transparent',
                  }}>
                  {Object.entries(cat.subfactors).map(([name, info]) => {
                    const isItemExpanded = info.issueId != null && expandedIssueId === info.issueId;
                    const hasDescription = !!info.description?.trim();
                    return (
                      <IssueRow
                        key={name}
                        testId={info.issueId ? `condition-issue-${info.issueId}` : undefined}
                        bodyTestId={
                          info.issueId ? `condition-issue-${info.issueId}-description` : undefined
                        }
                        title={name}
                        meta={[info.bt ? 'BigTicket' : null, info.cost !== 'N/A' ? info.cost : null]
                          .filter(Boolean)
                          .join(' · ')}
                        urgency={info.urgency}
                        urgencyTooltip={URGENCY_TIPS[info.urgency]}
                        expandable={Boolean(info.issueId)}
                        expanded={isItemExpanded}
                        onToggle={() =>
                          info.issueId && setExpandedIssueId(isItemExpanded ? null : info.issueId)
                        }
                        isMobile={isMobile}>
                        {hasDescription ? info.description : null}
                      </IssueRow>
                    );
                  })}
                </ScrollHintList>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Repair-cost-to-list-price ratio. Prefer the backend's authoritative value;
  // fall back to a local calc using the canonical price chain (Hauser-EQ price
  // first, then the property record, then 0).
  const repairPriceRatio = useMemo<number | null>(() => {
    const backendRatio = data?.condition_kpis?.repair_price_ratio;
    if (typeof backendRatio === 'number') return backendRatio;
    const totalCost = data?.condition_kpis?.total_repair_cost ?? 0;
    const listPrice = propertyDetails?.price ?? selectedProperty?.property.price ?? 0;
    if (listPrice <= 0 || totalCost <= 0) return null;
    return (totalCost / listPrice) * 100;
  }, [data?.condition_kpis, propertyDetails?.price, selectedProperty?.property.price]);

  const handleUpload = async (propertyId: string, file: File, source: 'seller' | 'buyer') => {
    // Bootstrap the sidebar processing indicator immediately so the user sees
    // feedback during the 5–30s window before the first SNS progress event
    // bumps stepProcessing through stages 2–4.
    beginProcessingIndicator();
    try {
      const [presigned] = await generatePreSignedUrls({
        files: [
          {
            filename: file.name,
            mimeType: file.type || 'application/octet-stream',
            isExtractable: true,
          },
        ],
      });
      await fetch(presigned.preSignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      await createDocuments({
        propertyId,
        source,
        documents: [
          {
            filename: file.name,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            s3Key: presigned.key,
          },
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
      showAlert(t('report.uploaded'), 'success');
    } catch {
      resetProcessing();
      showAlert(t('report.upload_failed'), 'error');
    }
  };

  // Big screens (27"-class: at least 1680 wide and 1000 tall) have room for the radar and the system rows together, so
  // the score card carries both; smaller computers split them, the rows moving to the reading column
  const fitsCombinedCard = () => window.innerWidth >= 1680 && window.innerHeight >= 1000;
  const [combinedCard, setCombinedCard] = useState(fitsCombinedCard);
  useEffect(() => {
    const onResize = () => setCombinedCard(fitsCombinedCard());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (isReportProcessing && data?.condition_score == null) {
    return (
      <Box
        sx={{
          p: isMobile ? '10px' : 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}>
        <ScoreProcessingBanner minHeight={300} />
      </Box>
    );
  }

  if (isNoListPrice) {
    // If Blueprint already has a price saved (e.g. a prior manual override), hauser-eq is
    // lagging on recalculation — show a spinner rather than re-prompting for a price.
    if (selectedProperty?.property.price) {
      return (
        <Box
          sx={{
            p: isMobile ? '10px' : 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 2,
          }}>
          <CircularProgress size={40} thickness={3} sx={{ color: '#C0FF02' }} />
          <span style={{ fontSize: 14, color: 'var(--tx-muted)' }}>Recalculating condition score…</span>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          p: isMobile ? '10px' : 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}>
        {selectedProperty && (
          <ManualPricePromptInline
            property={selectedProperty.property}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['properties'] });
              queryClient.invalidateQueries({
                queryKey: ['hauser-eq-results', propertyId],
              });
              showAlert(t('price.recalculating'), 'info');
            }}
          />
        )}
      </Box>
    );
  }

  if (hasNoReport) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {selectedProperty && (
            <UploadReportInline property={selectedProperty.property} onUpload={handleUpload} />
          )}
        </Box>
      </Box>
    );
  }

  if (layout === 'horizontal') {
    return (
      <Box
        data-testid="condition-score-panel-horizontal"
        sx={{ display: 'flex', gap: '32px', height: '100%' }}>
        {/* Left: title, score, grade, viz toggle, chart */}
        <Box sx={{ flex: '1 1 50%', minWidth: 0 }}>
          {/* Header: Title (left) + Score + /100 + Grade + viz toggle (right) —
              mirrors HomeScoreCard's header exactly (see HomeScoreCard.tsx). */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <div>
              <Tip text={SCORE_TOOLTIPS.ConditionScore}>
                <span style={{ fontSize: 22, fontWeight: 300, color: COLORS.text, cursor: 'help' }}>
                  The Home
                </span>
              </Tip>
              {conditionScoredAt && isValid(parseDate(conditionScoredAt)) && (
                <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                  Last scored{' '}
                  {parseDate(conditionScoredAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
              )}
              {processedDocs.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                  {processedDocs.map((r) => (
                    <span
                      key={r.id}
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: COLORS.textDim,
                        background: 'var(--tx-fill-2)',
                        border: 0,
                        borderRadius: 6,
                        padding: '2px 7px',
                        letterSpacing: '0.02em',
                      }}>
                      {DOCUMENT_TYPE_LABELS[r.documentType as DocumentClassificationType] ??
                        r.documentType}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              {/* Score + /100 + grade lockup only — scaled down 20% as one unit
                  so the proportions between them stay exact. Toggle icons below
                  are a separate control and stay full size. */}
              <div style={{ transform: 'scale(0.8)', transformOrigin: 'top right' }}>
                <div
                  style={{
                    fontFamily: VALUE_FONT,
                    fontSize: 72,
                    fontWeight: VALUE_WEIGHT,
                    letterSpacing: VALUE_TRACKING,
                    lineHeight: 1,
                    color: COLORS.text,
                  }}>
                  {conditionScore != null ? Math.round(conditionScore) : '--'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 8,
                    marginTop: 4,
                  }}>
                  <span style={{ fontSize: 17, color: COLORS.textDim }}>/100</span>
                  {grade && <GradeTag grade={grade} size="md" neutral />}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -10 }}>
                <div
                  style={{
                    display: 'flex',
                    background: 'var(--tx-fill)',
                    borderRadius: 10,
                    padding: 3,
                    gap: 2,
                  }}>
                  {(['spider', 'rings'] as const).map((mode) => {
                    const active = vizMode === mode;
                    const outerC = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                    const middleC = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                    const innerC = active
                      ? mode === 'spider'
                        ? '#FF9A4D'
                        : '#DB5282'
                      : 'rgba(255,255,255,0.35)';
                    const icon =
                      mode === 'spider' ? (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <polygon
                            points="7,1.5 11.8,4.25 11.8,9.75 7,12.5 2.2,9.75 2.2,4.25"
                            stroke={outerC}
                            strokeWidth="0.9"
                            fill="none"
                          />
                          <polygon
                            points="7,3.8 10,5.5 10,8.5 7,10.2 4,8.5 4,5.5"
                            stroke={middleC}
                            strokeWidth="0.9"
                            fill="none"
                          />
                          <polygon
                            points="7,5.5 8.6,6.4 8.6,7.6 7,8.5 5.4,7.6 5.4,6.4"
                            stroke={innerC}
                            strokeWidth="0.9"
                            fill="none"
                          />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="5.5" stroke={outerC} strokeWidth="1" />
                          <circle cx="7" cy="7" r="3.3" stroke={middleC} strokeWidth="1" />
                          <circle cx="7" cy="7" r="1.2" stroke={innerC} strokeWidth="1" />
                        </svg>
                      );
                    return (
                      <div
                        key={mode}
                        onClick={() => setVizMode(mode)}
                        style={{
                          padding: 5,
                          borderRadius: 7,
                          cursor: 'pointer',
                          background: active ? 'var(--tx-fill-3)' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.15s',
                          userSelect: 'none',
                        }}>
                        {icon}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Stack>

          <Box sx={{ position: 'relative', height: 380, mt: '58px' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: vizMode === 'spider' ? 1 : 0,
                pointerEvents: vizMode === 'spider' ? 'auto' : 'none',
                transition: 'opacity 0.2s',
              }}>
              <Box sx={{ transform: 'translate(-20px, -105px)' }}>
                <RadarChart
                  series={series}
                  size={550}
                  gridColor="rgba(255,255,255,0.1)"
                  labelColor={COLORS.textDim}
                  axisLabelColor="#BBB7AF"
                  gridLevels={4}
                  labelTooltips={COND_RADAR_TOOLTIPS}
                  // Default radiusFraction with labels shown is 0.3 — this scopes
                  // a 50%-larger plotted shape to just this instance (size still
                  // controls the overall canvas/label positions, unaffected).
                  radiusFraction={0.45}
                  // cornerRadius is a fixed px pullback, so the 50%-larger shape
                  // above needs a proportionally larger one too (default 5 * 1.5),
                  // or the vertex rounding reads as noticeably sharper than the
                  // main scorecard's radar (HomeScoreCard.tsx) at its default size.
                  cornerRadius={8}
                />
              </Box>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                opacity: vizMode === 'rings' ? 1 : 0,
                pointerEvents: vizMode === 'rings' ? 'auto' : 'none',
                transition: 'opacity 0.2s',
              }}>
              {/* RadarChart's own wrapper has hardcoded asymmetric -45px/-10px
                  margins (see RadarChart.tsx) that pull its visual center
                  17.5px above HealthRings' at an identical transform — see
                  HomeScoreCard.tsx's mobile toggle for the same compensation
                  applied in the other direction. */}
              <Box sx={{ transform: 'translate(-20px, -122.5px)' }}>
                <HealthRings data={healthRingsData} size={300} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right: category rows. A fixed top offset (not margin:'auto 0') —
            true vertical centering recalculates on every content-height
            change, so expanding a category would shift the whole cluster
            up/down each time. This nudges it down once and stays put;
            expanding just pushes content below it, top stays anchored. */}
        <Box
          sx={{
            flex: '1 1 50%',
            minWidth: 0,
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
          <Box sx={{ pt: '82px' }}>{renderCategoryRows()}</Box>
        </Box>
      </Box>
    );
  }

  if (scorecardOnly) {
    return (
      <Box
        data-testid="condition-score-dialog-card"
        sx={{ width: '100%', maxWidth: 760, mx: 'auto' }}>
        <HomeScoreCard
          footer={renderCategoryRows()}
          repairPreviewActive={repairPreviewActive}
          repairPreviewScore={repairPreview.score}
          repairPreviewCategoryScores={repairPreview.categoryScores}
        />
      </Box>
    );
  }

  return (
    <Box
      data-testid="condition-score-view"
      sx={{
        pt: 0,
        pb: isMobile ? '10px' : '20px',
        px: isMobile ? '10px' : 0,
      }}>
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <HomeScoreCard
            footer={renderCategoryRows()}
            repairPreviewActive={repairPreviewActive}
            repairPreviewScore={repairPreview.score}
            repairPreviewCategoryScores={repairPreview.categoryScores}
          />
          {permits && permits.length > 0 && <PermitHistoryCard permits={permits} />}
          <SubscoreDetailCard
            stories={condSubscoreStories}
            isLoading={isLoading}
            hasData={hasData}
            error={!!error}
            worstPillar={worstPillar ?? undefined}
            issueList={issueList}
            totalIssueCount={issues?.filter((i) => !i.resolved).length}
            rawUrgencyCounts={rawUrgencyCounts}
            baseScore={conditionScore ?? undefined}
            conditionHeadline={selectedProperty?.property.conditionHeadline}
            conditionBlurb={selectedProperty?.property.conditionBlurb}
            repairPreviewActive={repairPreviewActive}
            onRepairPreviewToggle={() => setRepairPreviewActive((active) => !active)}
          />
          {hoaData && (
            <HOAChecklistCard
              checklist={hoaData.hoaChecklist}
              findings={hoaData.hoaFindings}
              boardMinutesFindings={hoaData.hoaBoardMinutesFindings}
              environmentalFindings={hoaData.hoaEnvironmentalFindings}
              annualBudget={hoaData.hoaAnnualBudgetFindings}
            />
          )}
          <RepairHorizonCompact
            issues={issues}
            isLoading={isLoading}
            repairPriceRatio={repairPriceRatio}
          />
        </Box>
      ) : (
        <Grid container spacing="20px">
          {/* Left column */}
          {/* the instrument: the score card fills the scroll area under the pinned header exactly (the window less the
              20px gutter, 60px header, 20px gap and 20px bottom gutter) and stays put while the right column reads on; its
              radar carries the category scores on its axes */}
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{
              minWidth: 0,
              alignSelf: 'flex-start',
              position: 'sticky',
              top: 0,
              // 27" (combined): the column is exactly the window under the header, the same on The Home and The Area
              height: combinedCard ? 'calc(100dvh - 120px)' : 'max(460px, calc(100dvh - 120px))',
              // big screens: the column holds the card and the permit history; if that outgrows the window it scrolls
              // on its own so nothing pinned ends up out of reach
              ...(combinedCard && {
                maxHeight: 'calc(100dvh - 120px)',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }),
            }}>
            {combinedCard ? (
              <>
                <HomeScoreCard
                  fill
                  footer={renderCategoryRows()}
                  repairPreviewActive={repairPreviewActive}
                  repairPreviewScore={repairPreview.score}
                  repairPreviewCategoryScores={repairPreview.categoryScores}
                />
              </>
            ) : (
              <HomeScoreCard
                axisValues
                repairPreviewActive={repairPreviewActive}
                repairPreviewScore={repairPreview.score}
                repairPreviewCategoryScores={repairPreview.categoryScores}
              />
            )}
          </Grid>
          {/* Right column. 27" (combined): at least the window under the header, so its last card (Repair horizon) can
              stretch to end on the same line as the score card beside it */}
          <Grid
            size={{ sm: 12, md: 6 }}
            sx={{
              minWidth: 0,
              ...(combinedCard && { display: 'flex', flexDirection: 'column', minHeight: 'calc(100dvh - 120px)' }),
            }}>
            <SubscoreDetailCard
              stories={condSubscoreStories}
              isLoading={isLoading}
              hasData={hasData}
              error={!!error}
              worstPillar={worstPillar ?? undefined}
              issueList={issueList}
              totalIssueCount={issues?.filter((i) => !i.resolved).length}
              rawUrgencyCounts={rawUrgencyCounts}
              baseScore={conditionScore ?? undefined}
              conditionHeadline={selectedProperty?.property.conditionHeadline}
              conditionBlurb={selectedProperty?.property.conditionBlurb}
              repairPreviewActive={repairPreviewActive}
              onRepairPreviewToggle={() => setRepairPreviewActive((active) => !active)}
            />
            {/* By system: the category rows as the drill-down in the reading column, when the score card can't carry them */}
            {!combinedCard && (
            <Box
              data-testid="condition-by-system"
              sx={{
                mt: '20px',
                p: '20px',
                borderRadius: '24px',
                backgroundColor: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
              }}>
              {/* a card title (17/400, as Permit history and Repair horizon); eyebrows are for sections inside a card */}
              <Box sx={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)', mb: '8px' }}>
                By system
              </Box>
              {/* the rows bring their own 12px foot (for the score card's footer); cancel it so this card keeps an even 20 */}
              <Box sx={{ mx: '-20px', mb: '-12px' }}>{renderCategoryRows()}</Box>
            </Box>
            )}
            {permits && permits.length > 0 && (
              <Box sx={{ mt: '20px' }}>
                <PermitHistoryCard permits={permits} />
              </Box>
            )}

            {hoaData && (
              <Box sx={{ mt: '20px' }}>
                <HOAChecklistCard
                  checklist={hoaData.hoaChecklist}
                  findings={hoaData.hoaFindings}
                  boardMinutesFindings={hoaData.hoaBoardMinutesFindings}
                  environmentalFindings={hoaData.hoaEnvironmentalFindings}
                  annualBudget={hoaData.hoaAnnualBudgetFindings}
                />
              </Box>
            )}
            <Box
              sx={{
                mt: '20px',
                ...(combinedCard && { flex: 1, display: 'flex', flexDirection: 'column', '& > div': { flex: 1 } }),
              }}>
              <RepairHorizonCompact
                issues={issues}
                isLoading={isLoading}
                repairPriceRatio={repairPriceRatio}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
